import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAchievementCategory } from '../api/hooks';
import { BackIcon } from '../components/icons';
import { ShieldIcon } from '../components/achievements/icons';
import { accentOf, Medallion } from '../components/achievements/Medallion';
import { ErrorView } from '../components/StateViews';
import { backButton } from '../telegram';
import { seasonLabel, shortDate } from '../format';
import type { AchievementRow } from '../api/types';

type Filter = 'all' | 'progress' | 'done';

/** Экран категории: фильтр-чипы + строки достижений с медальоном-кольцом. */
export function AchievementCategoryScreen() {
  const navigate = useNavigate();
  const { pid: rawPid, catId = '' } = useParams();
  const pid = rawPid === undefined ? null : Number(rawPid);
  const category = useAchievementCategory(pid, catId);
  const [filter, setFilter] = useState<Filter>('all');

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  const base = pid === null ? '/achievements' : `/players/${pid}/achievements`;

  if (category.isLoading) {
    return (
      <div className="ach-screen">
        {Array.from({ length: 7 }, (_, i) => (
          <div key={i} className="skeleton" style={{ height: 62, borderRadius: 13, marginBottom: 9 }} />
        ))}
      </div>
    );
  }
  if (category.isError || !category.data) {
    return (
      <div className="ach-screen">
        <ErrorView message={(category.error as Error)?.message} />
      </div>
    );
  }

  const rows = category.data.rows;
  const done = rows.filter((r) => r.maxLevel > 0 && r.level >= r.maxLevel);
  // Закрытые сезоны (fixed/missed) и паузнутые прогрессировать не могут —
  // в «В процессе» только вечные и идущий сезон.
  const inProgress = rows.filter(
    (r) => r.level < r.maxLevel && (r.state === null || r.state === 'active') && !r.paused,
  );
  const visible = filter === 'done' ? done : filter === 'progress' ? inProgress : rows;

  // «Сезонные» — единственная категория с многосезонной историей: рисуем
  // секциями по сезонам, идущий сверху (у API порядок active → season DESC).
  const seasons =
    catId === 'seasonal'
      ? Array.from(new Set(visible.map((r) => r.seasonId ?? 0))).sort((a, b) => b - a)
      : null;

  // «Отбор» (SA2): плашка про сброс прогресса + группы разовые/ступенчатые.
  const isSelection = category.data.name === 'Отбор';
  const oneShots = isSelection ? visible.filter((r) => r.maxLevel <= 1) : [];
  const stepped = isSelection ? visible.filter((r) => r.maxLevel > 1) : [];

  return (
    <div className="ach-screen">
      <div className="subhead ach-head">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Назад">
          <BackIcon />
        </button>
        <div className="title">{category.data.name}</div>
      </div>

      <div className="ach-filters">
        {(
          [
            ['all', `Все ${rows.length}`],
            ['progress', `В процессе ${inProgress.length}`],
            ['done', `Готово ${done.length}`],
          ] as [Filter, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            className={`ach-filter${filter === key ? ' on' : ''}`}
            onClick={() => setFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {isSelection && (
        <div className="ach-sel-note">
          Прогресс считается внутри отбора. Полученное — остаётся.
        </div>
      )}

      <div className="ach-list">
        {isSelection ? (
          <>
            {oneShots.length > 0 && <div className="ach-season-head">Разовые</div>}
            {oneShots.map((r) => (
              <AchievementListRow key={r.id} r={r} base={base} selection />
            ))}
            {stepped.length > 0 && <div className="ach-season-head">Со ступенями</div>}
            {stepped.map((r) => (
              <AchievementListRow key={r.id} r={r} base={base} selection />
            ))}
          </>
        ) : seasons
          ? seasons.map((sid) => {
              const group = visible.filter((r) => (r.seasonId ?? 0) === sid);
              const live = group.some((r) => r.state === 'active');
              return (
                <div key={sid}>
                  <div className="ach-season-head">
                    {sid > 0 ? seasonLabel(sid) : `Сезон ${sid}`}
                    {live && <span className="live"> · идёт</span>}
                  </div>
                  {group.map((r) => (
                    <AchievementListRow key={r.id} r={r} base={base} />
                  ))}
                </div>
              );
            })
          : visible.map((r) => <AchievementListRow key={r.id} r={r} base={base} />)}
        {visible.length === 0 && <div className="ach-empty">Здесь пока пусто</div>}
      </div>
      <div className="ach-fade" />
    </div>
  );
}

function AchievementListRow({
  r,
  base,
  selection = false,
}: {
  r: AchievementRow;
  base: string;
  selection?: boolean;
}) {
  const doneRow = r.maxLevel > 0 && r.level >= r.maxLevel;
  // SA2: полученное разовое отбора — латунная подложка, справа дата.
  const brassRow = selection && r.maxLevel <= 1 && r.level >= 1;
  const pct =
    r.progress !== null && r.nextThreshold && r.nextThreshold > 0
      ? Math.min(1, r.progress / r.nextThreshold)
      : null;

  const sub = r.masked
    ? 'Секретное достижение'
    : r.paused
      ? 'Временно недоступно'
      : r.state === 'missed'
      ? 'Упущено'
      : r.state === 'fixed'
        ? 'Зафиксировано'
        : doneRow
          ? r.unlockedAt
            ? `Выполнено ${shortDate(r.unlockedAt)}`
            : 'Выполнено'
          : r.nextThreshold !== null
            ? r.progress !== null
              ? r.higherIsBetter
                ? `${r.progress} из ${r.nextThreshold}`
                : r.progress > 0
                  ? `Сейчас место ${r.progress} · цель топ-${r.nextThreshold}`
                  : `Цель: топ-${r.nextThreshold}`
              : `До уровня ${r.level + 1}: ${r.nextThreshold}`
            : (r.description ?? '');

  const cls = [
    'ach-row',
    doneRow ? 'ach-row--done' : '',
    r.state === 'active' ? 'ach-row--season' : '',
    r.state === 'missed' ? 'ach-row--missed' : '',
    r.paused ? 'ach-row--paused' : '',
    r.level === 0 && !doneRow ? 'ach-row--idle' : '',
    brassRow ? 'ach-row--brass' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Link className={cls} to={`${base}/${r.id}`}>
      <Medallion icon={r.icon} level={r.level} maxLevel={r.maxLevel} progress={pct}
        state={r.state} masked={r.masked} size={46}
        variant={accentOf(r.code, r.level, r.maxLevel)} />
      <div className="ach-row-main">
        <div className="nm">
          {r.name}
          {r.maxLevel > 1 && r.level > 0 && !r.masked && (
            <span className="lvl">
              {' '}
              · ур. {r.level}/{r.maxLevel}
            </span>
          )}
        </div>
        <div className="sub">{brassRow ? (r.description ?? 'Получено') : sub}</div>
      </div>
      {brassRow && r.unlockedAt ? (
        <div className="ach-row-date">{shortDate(r.unlockedAt)}</div>
      ) : (
        <div className="ach-row-pts">
          <ShieldIcon />
          {r.earnedPoints > 0 ? r.earnedPoints : r.totalPoints}
        </div>
      )}
    </Link>
  );
}
